<template>
  <div class="white-card">
    <v-row class="header" @click="onHeaderClick()">
      <v-col
        cols="3"
        :class="{
          'd-flex flex-column align-start': true,
          'justify-center': isSmallDeviceNotMobile
        }"
      >
        <div
          :class="{
            'd-flex align-center': true,
            'flex-column': isMobile || !isSmallDevice
          }"
        >
          <p>{{ $t('ticket.previewTitle') }}</p>
          <span v-if="isSmallDeviceNotMobile">&nbsp;</span>
          <p class="font-weight-bold">
            {{ ticketNumber > 0 ? ticketNumber : '-' }}
          </p>
        </div>
      </v-col>
      <v-col
        cols="6"
        :class="{
          'd-flex align-center pl-0': true,
          'flex-column': isMobile || !isSmallDevice,
          'justify-center': isSmallDeviceNotMobile
        }"
      >
        <p>{{ $t('cart.totalAmount') }}</p>
        <span v-if="isSmallDeviceNotMobile">&nbsp;</span>
        <p class="font-weight-bold">{{ ticketCost.net }} {{ currency }}</p>
      </v-col>
      <v-col v-if="isSmallDeviceNotMobile"></v-col>
      <v-col class="d-flex flex-column align-end px-0" @click="onBuyClick">
        <v-btn
          :outlined="buyIsDisabled"
          :disabled="buyIsDisabled"
          class="cart-button"
          :block="!isSmallDeviceNotMobile"
          depressed
          height="2.5rem"
          data-cy="buyNowButton"
          @click.stop="$emit('submit', discountCode)"
          >{{ $t('cart.buyNow') }}</v-btn
        >
      </v-col>
    </v-row>
    <div v-if="market === 'primary'">
      <hr class="dashed-line" />
      <p class="discount-label">{{ $t('vieworders.orderDetails.discount') }}</p>
      <div class="discount-description-label">
        {{ $t('cart.discountCodeDescription') }}
      </div>
      <div class="discount-input">
        <span class="p-float-label">
          <PInputText
            id="discountCode"
            v-model.trim="discountCode"
            class="full-width"
          />
          <label for="discountCode">{{ $t('discounts.code') }}</label>
        </span>
      </div>
    </div>
    <v-expand-transition>
      <div v-show="!isSmallDevice || expand">
        <div class="content">
          <div
            :class="{
              'scrollable-list': true,
              'scrollable-container': true
            }"
          >
            <v-row no-gutters>
              <v-col
                v-for="(ticket, index) of selectedTickets"
                :key="'ticket-card-' + index"
                cols="12"
                sm="6"
                lg="12"
                :class="{
                  'pl-6': isSmallDeviceNotMobile && index % 2 !== 0 && index > 0
                }"
              >
                <v-row
                  no-gutters
                  class="row-8 ticket-card d-flex align-start mb-3"
                >
                  <v-btn
                    icon
                    width="1.25rem"
                    height="1.25rem"
                    class="mx-1"
                    @click="$emit('remove', { ticket, index })"
                  >
                    <v-icon size="1.4rem" class="remove-icon-color"
                      >mdi-close-circle-outline</v-icon
                    >
                  </v-btn>
                  <v-col class="px-3">
                    <div class="single-ticket-preview">
                      <div class="single-ticket-preview__header">
                        <p>{{ ticket.name }}</p>
                        <div
                          v-if="
                            ticketHasAdditionalDetails(ticket) &&
                            market === 'primary'
                          "
                          class="single-ticket-preview__icon"
                          @click="toggleTicketDetails(ticket)"
                        >
                          <IconArrowDown
                            :class="{ expanded: ticket.expanded }"
                            size="24"
                          />
                        </div>
                      </div>
                      <div
                        v-if="ticket.expanded"
                        class="MOBILE_h12_Regular single-ticket-preview__additional-info"
                      >
                        <span v-if="ticket.seat">{{ ticket.seat.id }}</span>
                        <span v-if="ticket.discount">{{
                          ticket.discount.name
                        }}</span>
                        <span v-if="isDonation(ticket.pricingType)">{{
                          $t('ticketDonation.donation')
                        }}</span>
                        <div v-if="isRecurringOrHasTimeslots(ticket)">
                          <span v-if="ticket.occurrenceName"
                            >{{ ticket.occurrenceName }}
                            {{ ticket.timeslotName && '-' }}</span
                          >
                          <span v-if="ticket.timeslotName">{{
                            ticket.timeslotName
                          }}</span>
                        </div>
                        <div v-if="isRecurringOrHasTimeslots(ticket)">
                          <span v-if="onSameDay(ticket)">
                            {{ startDate(ticket).date }},
                            {{ startDate(ticket).time }} -
                            {{ endDate(ticket).time }}
                          </span>
                          <span v-else>
                            {{ startDate(ticket).date }},
                            {{ startDate(ticket).time }} -
                            {{ endDate(ticket).date }},
                            {{ endDate(ticket).time }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="auto" class="py-0 pl-3">
                    <p
                      v-if="isDonation(ticket.pricingType) && ticket.grossPrice"
                      class="text-right"
                    >
                      {{ ticket.grossPrice.toFixed(2) }}
                      {{ currency }}
                    </p>
                    <p v-else class="text-right">
                      {{ ticket.finalPrice ? ticket.finalPrice.toFixed(2) : 0 }}
                      {{ currency }}
                    </p>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row no-gutters class="mb-3 mt-5">
              <v-col cols="12" class="px-6"> </v-col>
            </v-row>
          </div>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
import PInputText from 'primevue/inputtext';
import IconArrowDown from '~/components/general/icons/IconArrowDown.vue';
import { PAYMENT_TYPES } from '~/utils/utils';
import { convertDateToDisplay } from '~/utils/date-time';

export default {
  name: 'TicketPricePreview',
  components: { IconArrowDown, PInputText },
  props: {
    ticketConfig: {
      type: Array,
      default() {
        return [];
      }
    },
    tickets: {
      type: Array,
      default() {
        return [];
      }
    },
    currency: {
      type: String,
      default() {
        return '';
      }
    },
    timezoneName: {
      type: String,
      default() {
        return '';
      }
    },
    format: {
      type: String,
      default() {
        return null;
      }
    },
    market: {
      type: String,
      default() {
        return 'primary';
      }
    },
    selectionValid: {
      type: Boolean,
      default: false
    },
    discountCodeValue: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      PAYMENT_TYPES,
      expand: false,
      discountCode: this.discountCodeValue,
      selectedTickets: this.tickets.map((ticket) => {
        return { ...ticket, expanded: false };
      })
    };
  },
  computed: {
    buyIsDisabled() {
      return this.ticketNumber === 0 || !this.selectionValid;
    },
    ticketsAreAvailable() {
      return this.ticketConfig.some((tc) => {
        return (
          tc.availableTickets > 0 && tc.quantity > 0 && !tc.ended && tc.started
        );
      });
    },
    isSmallDevice() {
      return this.$vuetify.breakpoint.mdAndDown;
    },
    isMobile() {
      return this.$vuetify.breakpoint.xsOnly;
    },
    isSmallDeviceNotMobile() {
      return this.isSmallDevice && !this.isMobile;
    },
    loggedIn() {
      return this.$store.state.auth.loggedIn;
    },
    ticketNumber() {
      return this.selectedTickets.length;
    },
    ticketCost() {
      return this.selectedTickets.reduce(
        (result, t) => {
          const net = parseFloat(this.ticketPrice(t)) + parseFloat(result.net);
          const isCrypto = this.$whitelabel.supportedCryptoCurrencies.includes(
            this.currency.toUpperCase()
          );
          return {
            net: net.toFixed(isCrypto ? 8 : 2),
            fees: result.fees + t.fees
          };
        },
        {
          net: 0,
          fees: 0
        }
      );
    }
  },
  watch: {
    tickets(newValue) {
      if (newValue) {
        this.selectedTickets = newValue.map((ticket) => {
          return { ...ticket, expanded: false };
        });
      }
    }
  },
  methods: {
    ticketHasAdditionalDetails(ticket) {
      return (
        ticket.seat ||
        ticket.discount ||
        this.isRecurringOrHasTimeslots(ticket) ||
        this.isDonation(ticket.pricingType)
      );
    },
    toggleTicketDetails(ticket) {
      ticket.expanded = !ticket.expanded;
    },
    isRecurringOrHasTimeslots(ticket) {
      return ticket.occurrenceName || ticket.timeslotName;
    },
    startDate(ticket) {
      let eventStartDate;
      if (ticket.timeslotStartAt) {
        eventStartDate = ticket.timeslotStartAt;
      } else if (ticket.occurrenceStartAt) {
        eventStartDate = ticket.occurrenceStartAt;
      }
      const startDate = convertDateToDisplay(eventStartDate, this.timezoneName);
      return {
        date: this.$d(startDate, 'DD_MM_YYYY'),
        time: this.$d(startDate, 'HH_mm')
      };
    },
    endDate(ticket) {
      let eventEndDate;
      if (ticket.timeslotEndAt) {
        eventEndDate = ticket.timeslotEndAt;
      } else if (ticket.occurrenceEndAt) {
        eventEndDate = ticket.occurrenceEndAt;
      }
      const endDate = convertDateToDisplay(eventEndDate, this.timezoneName);
      return {
        date: this.$d(endDate, 'DD_MM_YYYY'),
        time: this.$d(endDate, 'HH_mm')
      };
    },
    onSameDay(ticket) {
      return this.startDate(ticket).date === this.endDate(ticket).date;
    },
    isDonation(pricingType) {
      return pricingType === this.PAYMENT_TYPES.DONATION;
    },
    ticketPrice(ticket) {
      if (this.isDonation(ticket.pricingType)) {
        return ticket.donationAmount;
      }
      return ticket.finalPrice;
    },
    onHeaderClick() {
      if (this.ticketNumber !== 0) {
        this.expand = !this.expand;
      }
    },
    onBuyClick() {
      if (this.ticketNumber === 0 && this.ticketsAreAvailable) {
        this.$notifier.showMessage({
          content: this.$t('errors.emptyCart'),
          type: 'warning'
        });
      }
    }
  }
};
</script>

<style lang="scss">
.single-ticket-preview {
  &__header {
    display: flex;
    justify-content: space-between;
  }
  &__icon {
    height: 1.5rem;
    width: 1.5rem;
    svg {
      cursor: pointer;
      &:hover {
        opacity: 0.8;
      }
      transition: all 0.2s ease-in-out;
      &.expanded {
        transform: rotate(180deg);
      }
    }
  }
  &__additional-info {
    color: var(--v-textPrimary-base);
  }
}

.discount-description-label {
  color: var(--v-textPrimary-base);
  font-size: 12px;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  @media (max-width: 1240px) {
    padding: 0 56px 16px 56px;
  }
  @media (max-width: 900px) {
    padding: 0 44px 16px 44px;
  }
  @media (max-width: 600px) {
    padding: 0 16px 16px 16px;
  }
  padding-bottom: 16px;
}

.discount-input {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  @media (max-width: 1240px) {
    padding: 0 56px;
  }
  @media (max-width: 900px) {
    padding: 0 44px;
  }
  @media (max-width: 600px) {
    padding: 0 16px;
  }
}
.white-card {
  background-color: var(--v-backgroundPrimary-lighten5);
  border: solid 0.5px var(--v-borderPrimary-lighten4);
  border-radius: 4px;
  .header {
    padding: 1rem 1.5rem;
    @media (max-width: 1240px) {
      padding: 0 56px;
    }
    @media (max-width: 900px) {
      padding: 0 44px;
    }
    @media (max-width: 600px) {
      padding: 0 16px;
    }
    margin: 0;
    p {
      color: var(--v-textPrimary-base);
      font-size: 0.875rem;
      line-height: 110%;
      text-align: center;
      @media (max-width: 1240px) {
        font-size: 1rem;
      }
    }
  }
  .content {
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
    .scrollable-container {
      padding-left: 1.5rem;
      padding-right: 1.5rem;
      @media (max-width: 1240px) {
        padding: 0 56px;
      }
      @media (max-width: 900px) {
        padding: 0 44px;
      }
      @media (max-width: 600px) {
        padding: 0 16px;
      }
    }
  }
  .discount-label {
    font-weight: bold;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    @media (max-width: 1240px) {
      padding: 0 56px;
    }
    @media (max-width: 900px) {
      padding: 0 44px;
    }
    @media (max-width: 600px) {
      padding: 0 16px;
    }
  }
  p {
    color: var(--v-textPrimary-base);
    font-size: 1rem;
    line-height: 130%;
    min-height: 1.3rem;
    margin-bottom: 0;
  }
  .cart-info {
    font-size: 0.75rem;
    line-height: 120%;
    text-align: center;
    opacity: 0.5;
  }
}
.ticket-card {
  background-color: var(--v-backgroundPrimary-lighten5);
  border-radius: 0.25rem;
  filter: drop-shadow(0rem 0.25rem 0.25rem rgba(0, 0, 0, 0.25));
  padding: 1rem 1.5rem 1rem 2rem;
  position: relative;
}
.cart-button.v-btn:not(.v-btn--round).v-size--default {
  background-color: var(--v-buttonGray-lighten1);
  padding: 0;
  @media (max-width: 1240px) {
    padding: 0 2.875rem;
  }
  @media (max-width: 600px) {
    padding: 0;
  }
}

.scrollable-list {
  overflow-y: auto;
  max-height: 40vh;
}
.remove-icon-color {
  color: var(--v-buttonHighlightError-base) !important;
}

.dashed-line {
  border: 1px dashed var(--v-backgroundPrimary-lighten4);
  margin: 0 1.5rem 16px 1.5rem;
}
</style>
